import { useCallback, useEffect, useRef } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';

import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation';
import type { GeneratedVisualBlockCounts } from '@atlassian/rovo-content-bridge-api-commands/commands/generated-visual-block-counts';

import { expValEquals } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import {
	computeRetentionRatio,
	extractPlainText,
	RETENTION_DELAY_CONFIG_KEY,
	RETENTION_MEASUREMENT_DELAY_MS,
	RETENTION_THRESHOLD,
} from '../utils/content-retention.ts';
import { getDocument, isEmptyDocument } from '../utils/bridge-document-utils.ts';
import type { UseRovoBridgeCommandListenerParams } from '../RovoBridgeCommandListener.tsx';

type EditorAPI = UseRovoBridgeCommandListenerParams['editorAPI'];

type PendingMeasurement = {
	timerId: ReturnType<typeof setTimeout>;
	originalText: string;
	originalLength: number;
	measurementDelayMs: number;
	singleInstrumentationId?: string;
	conversationChannelId?: string;
	entryPoint?: string;
	visualBlockCounts?: GeneratedVisualBlockCounts;
};

type Params = {
	editorAPI: EditorAPI;
	contentId: string;
};

type TrackParams = {
	singleInstrumentationId?: string;
	conversationChannelId?: string;
	adfDoc?: DocNode;
	entryPoint?: string;
	visualBlockCounts?: GeneratedVisualBlockCounts;
};

const BLANK_OBJECT_ENTRY_POINT = 'cwrBlankObject';

/**
 * Measures whether content streamed into the page (e.g. Create with Rovo / BOE)
 * survives a short window after the stream completes. A wiped or undone page is
 * a strong rejection signal; edits and additions count as acceptance.
 *
 * The retention event is keyed by `singleInstrumentationId` so it can be joined
 * to the AI `viewed` event at analysis time.
 *
 * For the blank object experience there is no explicit accept action, so retained
 * content also fires the `aiChat` unified `aiResult actioned` event that the other
 * Create with Rovo entry points emit on "Add to Confluence". This lets BOE be included
 * in the CWR acceptance rate without counting every completed stream as accepted.
 */
export const useStreamedContentRetentionMetric = ({
	editorAPI,
	contentId,
}: Params): {
	trackContentRetention: ({
		singleInstrumentationId,
		conversationChannelId,
		adfDoc,
		entryPoint,
		visualBlockCounts,
	}: TrackParams) => Promise<void>;
} => {
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();
	const pendingRef = useRef<PendingMeasurement | null>(null);

	// Keep the latest contentId/editorAPI reachable from the timer callback
	// without re-creating the tracking callback on every change.
	const contentIdRef = useRef(contentId);
	contentIdRef.current = contentId;
	const editorAPIRef = useRef(editorAPI);
	editorAPIRef.current = editorAPI;

	const emitRetentionEvent = useCallback(
		(attributes: Record<string, unknown>) => {
			sendAnalyticsEvent({
				eventType: 'track',
				actionSubject: 'smartCreateContent',
				action: 'contentRetentionMeasured',
				attributes: {
					createdContentProduct: 'confluence',
					createdContentType: 'page',
					contentId: contentIdRef.current,
					...attributes,
				},
			});
		},
		[sendAnalyticsEvent],
	);

	const runMeasurement = useCallback(async () => {
		const pending = pendingRef.current;
		if (!pending) {
			return;
		}
		pendingRef.current = null;

		const doc = editorAPIRef.current ? await getDocument(editorAPIRef.current) : null;
		const emptied = doc ? isEmptyDocument(doc) : true;
		const currentText = doc ? extractPlainText(doc) : '';
		const retentionRatio = computeRetentionRatio(pending.originalText, currentText);
		const retained = !emptied && retentionRatio >= RETENTION_THRESHOLD;

		emitRetentionEvent({
			singleInstrumentationId: pending.singleInstrumentationId,
			measurementDelayMs: pending.measurementDelayMs,
			streamedContentLength: pending.originalLength,
			finalContentLength: currentText.length,
			retentionRatio,
			retained,
			emptied,
		});

		if (
			retained &&
			pending.entryPoint === BLANK_OBJECT_ENTRY_POINT &&
			expValEquals('aifc_boe_ai_result_actioned', 'isEnabled', true)
		) {
			trackAIMAUEvent({
				type: 'actioned',
				actionResult: 'contentRetained',
				invokedFrom: 'AIFC',
				proactive: false,
				userGenerated: true,
				singleInstrumentationID: pending.singleInstrumentationId,
				...(pending.conversationChannelId &&
					expValEquals('cwr_boe_include_channel_id', 'isEnabled', true) && {
						conversationChannelId: pending.conversationChannelId,
					}),
				attributes: {
					entryPoint: pending.entryPoint,
					isInSmartCreateFlow: true,
					createdContentProduct: 'confluence',
					createdContentType: 'page',
					// This event only follows content streamed into the iframe. Server-side
					// execution bypasses that stream entirely, so retained content here is
					// always from frontend execution.
					serverSideExecution: false,
					contentId: contentIdRef.current,
					retentionRatio,
					measurementDelayMs: pending.measurementDelayMs,
					...pending.visualBlockCounts,
				},
			});
		}
	}, [emitRetentionEvent, trackAIMAUEvent]);

	const trackContentRetention = useCallback(
		async ({
			singleInstrumentationId,
			conversationChannelId,
			adfDoc,
			entryPoint,
			visualBlockCounts,
		}: TrackParams): Promise<void> => {
			const api = editorAPIRef.current;
			if (!api) {
				return;
			}

			const doc = adfDoc ?? (await getDocument(api));
			if (!doc || isEmptyDocument(doc)) {
				return;
			}
			const originalText = extractPlainText(doc);

			// Tunable post-stream window; defaults to RETENTION_MEASUREMENT_DELAY_MS.
			const measurementDelayMs = StatsigConfigurations.getNumber(
				RETENTION_DELAY_CONFIG_KEY,
				RETENTION_MEASUREMENT_DELAY_MS,
			);

			// Replace any in-flight measurement (e.g. a re-stream into the same page).
			if (pendingRef.current) {
				clearTimeout(pendingRef.current.timerId);
			}
			const timerId = setTimeout(() => {
				void runMeasurement();
			}, measurementDelayMs);
			pendingRef.current = {
				timerId,
				originalText,
				originalLength: originalText.length,
				measurementDelayMs,
				singleInstrumentationId,
				conversationChannelId,
				entryPoint,
				visualBlockCounts,
			};
		},
		[runMeasurement],
	);

	// On unmount (e.g. SPA navigation away before the window elapses) cancel any
	// pending measurement so the timer can't fire after the editor is gone.
	useEffect(() => {
		return () => {
			if (pendingRef.current) {
				clearTimeout(pendingRef.current.timerId);
				pendingRef.current = null;
			}
		};
	}, []);

	return { trackContentRetention };
};
